/**
 * @file DatabaseOwners.js
 * WHAT: CRUD management interface for database owner records (DBA/system accounts) with single and bulk registration, attester assignment, and enterprise user lookup.
 * WHY: Database owners (SYS, SYSTEM, sa, cassandra, etc.) must be registered so the account classification framework can exclude them from attestation campaigns and route system account reviews to designated attesters.
 */

import React, { useState, useEffect } from 'react';
import SearchableDropdown from './SearchableDropdown';

import API_CONFIG from '../config/api.config';
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || API_CONFIG.getEndpoint('/api/databases');

/**
 * WHAT: Renders a paginated, filterable table of database owner records with create/edit/delete modals and a bulk registration flow for assigning attesters to system accounts.
 * WHY: Properly classifying system/DBA accounts prevents them from appearing as unattested in compliance reports, and assigning attesters ensures someone is responsible for reviewing privileged system account access.
 */
const DatabaseOwner = () => {
  const [owners, setOwners] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingOwner, setEditingOwner] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    database_name: '',
    username: '',
    is_active: ''
  });
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [enterpriseUsers, setEnterpriseUsers] = useState([]);
  const [databases, setDatabases] = useState([]);
  const [databaseAccounts, setDatabaseAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAccounts, setBulkAccounts] = useState([]);
  const [selectedBulkAccounts, setSelectedBulkAccounts] = useState([]);
  const [bulkFormData, setBulkFormData] = useState({
    database_type: '',
    database_name: '',
    attester_id: '',
    override_reason: 'System/Default account owner assignment'
  });
  const [loadingBulkAccounts, setLoadingBulkAccounts] = useState(false);

  // System Account Managers: per-technology default manager config + Apply flow.
  const [sysMgrDefaults, setSysMgrDefaults] = useState([]); // [{database_type, attester_id, attester_name, ...}]
  const [sysMgrSaving, setSysMgrSaving] = useState('');     // database_type currently saving
  const [sysMgrApplyResult, setSysMgrApplyResult] = useState(null); // last apply/dry-run payload
  const [sysMgrApplying, setSysMgrApplying] = useState(false);

  // Form state for create/edit
  const [formData, setFormData] = useState({
    database_name: '',
    database_type: '',
    username: '',
    attester_id: '',
    override_reason: '',
    effective_date: '',
    expiry_date: '',
    is_active: 'true'
  });

  useEffect(() => {
    loadOwners();
  }, [filters]);

  useEffect(() => {
    loadEnterpriseUsers();
    loadDatabases();
    loadSysMgrDefaults();
  }, []);

  const loadOwners = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (filters.database_name) params.append('database_name', filters.database_name);
      if (filters.username) params.append('username', filters.username);
      if (filters.is_active) params.append('is_active', filters.is_active);

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`database-owners/?${params.toString()}`));
      const data = await response.json();

      if (data.success) {
        setOwners(data.owners);
      } else {
        setError('Failed to load database owners');
      }
    } catch (err) {
      setError('Error loading database owners: ' + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadEnterpriseUsers = async () => {
    try {
      // Fetch all enterprise users with a large page size to avoid pagination issues
      const response = await fetch(API_CONFIG.getEnterpriseUsersEndpoint('?page_size=1000'));
      const data = await response.json();

      // API returns paginated results with 'results' array
      if (data.results) {
        setEnterpriseUsers(data.results);
      } else if (data.success) {
        // Fallback for non-paginated response format
        setEnterpriseUsers(data.users);
      } else {
        console.error('Failed to load enterprise users');
      }
    } catch (err) {
      console.error('Error loading enterprise users:', err.message);
    }
  };

  const loadDatabases = async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('database-owners/databases-list/'));
      const data = await response.json();

      if (data.success) {
        setDatabases(data.databases);
      } else {
        console.error('Failed to load databases');
      }
    } catch (err) {
      console.error('Error loading databases:', err.message);
    }
  };

  // ---- System Account Managers (per-tech default manager) -----------------
  const loadSysMgrDefaults = async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('database-owners/system-manager-defaults/'));
      const data = await response.json();
      if (data.success) setSysMgrDefaults(data.defaults || []);
    } catch (err) {
      console.error('Error loading system manager defaults:', err.message);
    }
  };

  // Save (or clear) one technology's default manager.
  //
  // Update ONLY the affected row in local state (optimistic) and DON'T refetch
  // the whole defaults list — refetching replaced the array on every keystroke-
  // free selection, remounting every SearchableDropdown and causing a visible
  // flicker. We resolve the display fields locally from the already-loaded
  // enterpriseUsers so the row reflects the new manager immediately. The PUT/
  // DELETE still persists; on error we surface it and reload to resync.
  const saveSysMgrDefault = async (databaseType, attesterId) => {
    // Skip no-op selections (the dropdown's value-change can fire with the same id).
    const current = sysMgrDefaults.find(r => r.database_type === databaseType);
    if (current && (current.attester_id || '') === (attesterId || '')) return;

    const matched = attesterId ? enterpriseUsers.find(u => u.employee_id === attesterId) : null;
    setSysMgrDefaults(prev => prev.map(r => r.database_type === databaseType ? {
      ...r,
      attester_id: attesterId || null,
      attester_name: matched ? (matched.display_name || matched.employee_id) : null,
      attester_email: matched ? matched.email : null,
      attester_job_title: matched ? matched.job_title : null,
    } : r));

    setSysMgrSaving(databaseType);
    try {
      if (attesterId) {
        await fetch(API_CONFIG.getDatabasesEndpoint(`database-owners/system-manager-defaults/${encodeURIComponent(databaseType)}/set/`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attester_id: attesterId })
        });
      } else {
        await fetch(API_CONFIG.getDatabasesEndpoint(`database-owners/system-manager-defaults/${encodeURIComponent(databaseType)}/delete/`), {
          method: 'DELETE'
        });
      }
    } catch (err) {
      setError('Error saving system manager: ' + err.message);
      loadSysMgrDefaults(); // resync on failure
    } finally {
      setSysMgrSaving('');
    }
  };

  // Apply configured managers to all system accounts. dryRun=true previews counts.
  const applySysMgrDefaults = async (dryRun) => {
    setSysMgrApplying(true);
    setSysMgrApplyResult(null);
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('database-owners/system-manager-defaults/apply/'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ dry_run: dryRun })
      });
      const data = await response.json();
      setSysMgrApplyResult(data);
      if (!dryRun && data.success) {
        loadOwners(); // refresh the main table with the newly-created owner rows
      }
    } catch (err) {
      setError('Error applying system managers: ' + err.message);
    } finally {
      setSysMgrApplying(false);
    }
  };

  const loadDatabaseAccounts = async (databaseType, databaseName) => {
    if (!databaseType || !databaseName) {
      setDatabaseAccounts([]);
      return;
    }

    setLoadingAccounts(true);
    try {
      const params = new URLSearchParams();
      params.append('database_type', databaseType);
      params.append('database_name', databaseName);

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`database-owners/accounts-list/?${params.toString()}`));
      const data = await response.json();

      if (data.success) {
        setDatabaseAccounts(data.accounts);
      } else {
        console.error('Failed to load database accounts');
        setDatabaseAccounts([]);
      }
    } catch (err) {
      console.error('Error loading database accounts:', err.message);
      setDatabaseAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const loadBulkAccounts = async (databaseType, databaseName) => {
    if (!databaseType || !databaseName) {
      setBulkAccounts([]);
      setSelectedBulkAccounts([]);
      return;
    }

    setLoadingBulkAccounts(true);
    try {
      const params = new URLSearchParams();
      params.append('database_type', databaseType);
      params.append('database_name', databaseName);

      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`database-owners/accounts-list/?${params.toString()}`));
      const data = await response.json();

      if (data.success) {
        setBulkAccounts(data.accounts);
        // Pre-select system accounts
        const systemAccounts = data.accounts
          .filter(acc => acc.is_system_account)
          .map(acc => `${acc.username}|${acc.database_name}`);
        setSelectedBulkAccounts(systemAccounts);
      } else {
        console.error('Failed to load bulk accounts');
        setBulkAccounts([]);
      }
    } catch (err) {
      console.error('Error loading bulk accounts:', err.message);
      setBulkAccounts([]);
    } finally {
      setLoadingBulkAccounts(false);
    }
  };

  const handleBulkAccountToggle = (username, databaseName) => {
    const key = `${username}|${databaseName}`;
    setSelectedBulkAccounts(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const handleSelectAllSystemAccounts = () => {
    const systemAccounts = bulkAccounts
      .filter(acc => acc.is_system_account)
      .map(acc => `${acc.username}|${acc.database_name}`);
    setSelectedBulkAccounts(systemAccounts);
  };

  const handleSelectAllAccounts = () => {
    const allAccounts = bulkAccounts.map(acc => `${acc.username}|${acc.database_name}`);
    setSelectedBulkAccounts(allAccounts);
  };

  const handleDeselectAllAccounts = () => {
    setSelectedBulkAccounts([]);
  };

  const handleBulkCreate = async () => {
    if (!bulkFormData.attester_id || selectedBulkAccounts.length === 0) {
      setError('Please select an attester and at least one account');
      return;
    }

    try {
      const accounts = selectedBulkAccounts.map(key => {
        const [username, database_name] = key.split('|');
        return { username, database_name };
      });

      const response = await fetch(API_CONFIG.getDatabasesEndpoint('database-owners/bulk-create/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          database_type: bulkFormData.database_type,
          attester_id: bulkFormData.attester_id,
          accounts: accounts,
          override_reason: bulkFormData.override_reason,
          created_by: 'ADMIN_USER'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setShowBulkModal(false);
        setBulkFormData({
          database_type: '',
          database_name: '',
          attester_id: '',
          override_reason: 'System/Default account owner assignment'
        });
        setBulkAccounts([]);
        setSelectedBulkAccounts([]);
        loadOwners();
        alert(`Bulk assignment complete!\nCreated: ${data.created}\nSkipped (already exist): ${data.skipped}`);
      } else {
        setError(data.error || 'Failed to bulk create database owners');
      }
    } catch (err) {
      setError('Error bulk creating database owners: ' + err.message);
    }
  };

  const handleCreateOwner = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('database-owners/create/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          created_by: 'ADMIN_USER'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setShowCreateModal(false);
        resetCreateForm();
        loadOwners();
      } else {
        setError(data.error || 'Failed to create database owner');
      }
    } catch (err) {
      setError('Error creating database owner: ' + err.message);
    }
  };

  const handleEditOwner = async (e) => {
    e.preventDefault();
    try {
      const ownerId = `${editingOwner.database_name}|${editingOwner.database_type}|${editingOwner.username}`;
      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`database-owners/${encodeURIComponent(ownerId)}/update/`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          last_modified_by: 'ADMIN_USER'
        }),
      });

      const data = await response.json();

      if (data.success) {
        setShowEditModal(false);
        setEditingOwner(null);
        loadOwners();
      } else {
        setError(data.error || 'Failed to update database owner');
      }
    } catch (err) {
      setError('Error updating database owner: ' + err.message);
    }
  };

  const handleDeleteOwner = async (owner) => {
    if (!window.confirm('Are you sure you want to delete this database owner?')) {
      return;
    }

    try {
      const ownerId = `${owner.database_name}|${owner.database_type}|${owner.username}`;
      const response = await fetch(API_CONFIG.getDatabasesEndpoint(`database-owners/${encodeURIComponent(ownerId)}/delete/`), {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        loadOwners();
      } else {
        setError(data.error || 'Failed to delete database owner');
      }
    } catch (err) {
      setError('Error deleting database owner: ' + err.message);
    }
  };

  const openEditModal = (owner) => {
    setEditingOwner(owner);

    setFormData({
      database_name: owner.database_name,
      database_type: owner.database_type,
      username: owner.username,
      attester_id: owner.attester_id || '',
      override_reason: owner.override_reason || '',
      effective_date: owner.effective_date ? owner.effective_date.split('T')[0] : '',
      expiry_date: owner.expiry_date ? owner.expiry_date.split('T')[0] : '',
      is_active: owner.is_active ? 'true' : 'false'
    });
    setShowEditModal(true);
  };

  const resetCreateForm = () => {
    setFormData({
      database_name: '',
      database_type: '',
      username: '',
      attester_id: '',
      override_reason: '',
      effective_date: '',
      expiry_date: '',
      is_active: 'true'
    });
    setDatabaseAccounts([]);
  };

  // CSV Export
  const handleExportCSV = async () => {
    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('database-owners/export-csv/'));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'database_owners_export.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Error exporting CSV: ' + err.message);
    }
  };

  // CSV Import
  const handleImportCSV = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch(API_CONFIG.getDatabasesEndpoint('database-owners/import-csv/'), {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        alert(`Import successful!\nAdded: ${data.added}\nUpdated: ${data.updated}`);
        loadOwners();
      } else {
        setError(data.error || 'Import failed');
      }
    } catch (err) {
      setError('Error importing CSV: ' + err.message);
    }

    // Reset file input
    event.target.value = '';
  };

  // Get unique database types. Deduped case-INSENSITIVELY: app_database_mapping
  // has historically held mixed casing for the same tech (e.g. 'MySQL' and
  // 'MYSQL'), which a plain Set would surface as two dropdown options. We keep
  // the first-seen casing as the display value.
  const getUniqueDatabaseTypes = () => {
    const seen = new Map(); // UPPER -> first-seen display value
    databases.forEach(db => {
      const t = db.database_type;
      if (t && !seen.has(t.toUpperCase())) seen.set(t.toUpperCase(), t);
    });
    return [...seen.values()].sort();
  };

  // Get databases filtered by selected database_type
  const getFilteredDatabases = () => {
    if (!formData.database_type) {
      return databases;
    }
    return databases.filter(db => db.database_type === formData.database_type);
  };

  // Format database option for dropdown.
  // M:N: one option per database; show ALL linked app names so searching by any
  // linked app (not just the primary) still finds the database.
  const formatDatabaseOption = (db) => {
    const apps = db.application_names || (db.app_id ? `${db.app_id} - ${db.app_name}` : '');
    return apps ? `${db.database_name} (${apps})` : db.database_name;
  };

  // Helper function to get attester name
  const getAttesterName = (attesterId) => {
    const user = enterpriseUsers.find(u => u.employee_id === attesterId);
    return user ? user.display_name : '';
  };

  // Sorting function
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Sort and paginate owners
  const getSortedOwners = () => {
    let sortedData = [...owners];

    if (sortConfig.key) {
      sortedData.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle attester name sorting
        if (sortConfig.key === 'attester_name') {
          aValue = getAttesterName(a.attester_id);
          bValue = getAttesterName(b.attester_id);
        }

        // Handle date sorting
        if (sortConfig.key === 'effective_date' || sortConfig.key === 'expiry_date') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return sortedData;
  };

  const sortedOwners = getSortedOwners();
  const totalPages = Math.ceil(sortedOwners.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOwners = sortedOwners.slice(startIndex, endIndex);

  // Sort icon helper
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return <i className="fas fa-sort text-muted ms-1"></i>;
    }
    return sortConfig.direction === 'asc' ?
      <i className="fas fa-sort-up ms-1"></i> :
      <i className="fas fa-sort-down ms-1"></i>;
  };

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="fas fa-database text-primary me-2"></i>
              Database Owner Management
            </h2>
            <div>
              <button
                className="btn btn-outline-primary me-2"
                onClick={loadOwners}
                disabled={isLoading}
              >
                <i className="fas fa-sync-alt me-2"></i>
                Refresh
              </button>
              <button
                className="btn btn-outline-success me-2"
                onClick={handleExportCSV}
              >
                <i className="fas fa-download me-2"></i>
                Download CSV
              </button>
              <label className="btn btn-outline-info me-2" style={{ marginBottom: 0 }}>
                <i className="fas fa-upload me-2"></i>
                Upload CSV
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleImportCSV}
                  style={{ display: 'none' }}
                />
              </label>
              <button
                className="btn btn-warning me-2"
                onClick={() => {
                  setBulkFormData({
                    database_type: '',
                    database_name: '',
                    attester_id: '',
                    override_reason: 'System/Default account owner assignment'
                  });
                  setBulkAccounts([]);
                  setSelectedBulkAccounts([]);
                  setShowBulkModal(true);
                }}
              >
                <i className="fas fa-users-cog me-2"></i>
                Bulk Assign
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  resetCreateForm();
                  setShowCreateModal(true);
                }}
              >
                <i className="fas fa-plus me-2"></i>
                Create Owner
              </button>
            </div>
          </div>

          {/* Routing-model banner — placed at the top of the page so the model
              (defaults below + overrides above) is the first thing readers see. */}
          <div className="alert alert-info border-2 shadow-sm mb-4 p-3" role="alert">
            <div className="d-flex align-items-start">
              <i className="fas fa-info-circle fa-2x text-info me-3 mt-1"></i>
              <div style={{ fontSize: '1rem', lineHeight: 1.55 }}>
                <div className="fw-bold fs-5 mb-1">How attester routing works</div>
                Configure default managers in the <strong>System Account Managers</strong> grid
                below. Database internal / system accounts will be routed to that manager as their
                attester at campaign time.
                <div className="mt-2">
                  <strong>For per-account exceptions</strong> — when a specific account needs a
                  different attester — add a row to the Database Owners table above using
                  <strong> Bulk Assign</strong> or <strong>Create Owner</strong>.
                </div>
              </div>
            </div>
          </div>

          {error && (
            <div className="alert alert-danger" role="alert">
              <i className="fas fa-exclamation-triangle me-2"></i>
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
              ></button>
            </div>
          )}

          {/* Filters — single-line bar (no surrounding card) to keep the page compact */}
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <span className="text-muted me-1"><i className="fas fa-filter me-1"></i>Filters:</span>
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: '240px' }}
              placeholder="Database name…"
              value={filters.database_name}
              onChange={(e) => setFilters(prev => ({ ...prev, database_name: e.target.value }))}
            />
            <input
              type="text"
              className="form-control form-control-sm"
              style={{ maxWidth: '240px' }}
              placeholder="Username…"
              value={filters.username}
              onChange={(e) => setFilters(prev => ({ ...prev, username: e.target.value }))}
            />
            <select
              className="form-select form-select-sm"
              style={{ maxWidth: '180px' }}
              value={filters.is_active}
              onChange={(e) => setFilters(prev => ({ ...prev, is_active: e.target.value }))}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <button
              className="btn btn-sm btn-outline-secondary"
              onClick={() => setFilters({ database_name: '', username: '', is_active: '' })}
            >
              <i className="fas fa-times me-1"></i>
              Clear
            </button>
          </div>

          {/* Main Table */}
          <div className="card">
            <div className="card-header">
              <h6 className="mb-0">
                <i className="fas fa-table me-2"></i>
                Database Owners ({owners.length} records)
              </h6>
            </div>
            <div className="card-body">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <div className="mt-2">Loading database owners...</div>
                </div>
              ) : (
                <>
                  <div className="table-responsive">
                    <table className="table table-striped table-hover">
                      <thead className="table-dark">
                        <tr>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('database_name')}>
                            Database {getSortIcon('database_name')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('username')}>
                            Username {getSortIcon('username')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('attester_id')}>
                            Employee ID {getSortIcon('attester_id')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('attester_name')}>
                            Attester Name {getSortIcon('attester_name')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('override_reason')}>
                            Reason {getSortIcon('override_reason')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('effective_date')}>
                            Effective Date {getSortIcon('effective_date')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('expiry_date')}>
                            Expiry Date {getSortIcon('expiry_date')}
                          </th>
                          <th style={{ cursor: 'pointer' }} onClick={() => handleSort('is_active')}>
                            Status {getSortIcon('is_active')}
                          </th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentOwners.length === 0 ? (
                          <tr>
                            <td colSpan="10" className="text-center py-4">
                              <i className="fas fa-inbox text-muted me-2"></i>
                              No database owners found
                            </td>
                          </tr>
                        ) : (
                          currentOwners.map((owner, index) => (
                            <tr key={index}>
                              <td>
                                <div className="fw-semibold">{owner.database_name}</div>
                                <small className="text-muted">{owner.database_type}</small>
                              </td>
                              <td>
                                <code style={{ fontSize: '1rem', fontWeight: 700, color: '#212529' }}>{owner.username}</code>
                              </td>
                              <td>
                                <div>
                                  <span className="text-info">{owner.attester_id}</span>
                                  {owner.attester_job_title && (
                                    <div><small className="text-muted">{owner.attester_job_title}</small></div>
                                  )}
                                  {owner.attester_department && (
                                    <div><small className="text-muted">{owner.attester_department}</small></div>
                                  )}
                                </div>
                              </td>
                              <td>
                                {owner.attester_name ? (
                                  <div>
                                    <div className="fw-semibold">{owner.attester_name}</div>
                                    <small className="text-muted">{owner.attester_email}</small>
                                  </div>
                                ) : (
                                  <span className="text-muted">-</span>
                                )}
                              </td>
                              <td>
                                {owner.override_reason || <span className="text-muted">-</span>}
                              </td>
                              <td>
                                {owner.effective_date ? new Date(owner.effective_date).toLocaleDateString() : '-'}
                              </td>
                              <td>
                                {owner.expiry_date ? new Date(owner.expiry_date).toLocaleDateString() : 'No expiry'}
                              </td>
                              <td>
                                <span className={owner.is_active === 'true' ? 'text-success' : 'text-secondary'}>
                                  {owner.is_active === 'true' ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                              <td>
                                <div className="btn-group btn-group-sm">
                                  <button
                                    className="btn btn-outline-primary"
                                    onClick={() => openEditModal(owner)}
                                    title="Edit Owner"
                                  >
                                    <i className="fas fa-edit"></i>
                                  </button>
                                  <button
                                    className="btn btn-outline-danger"
                                    onClick={() => handleDeleteOwner(owner)}
                                    title="Delete Owner"
                                  >
                                    <i className="fas fa-trash"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <nav aria-label="Pagination">
                      <ul className="pagination justify-content-center mt-3">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Previous
                          </button>
                        </li>
                        {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                          <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button
                            className="page-link"
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Next
                          </button>
                        </li>
                      </ul>
                    </nav>
                  )}
                </>
              )}
            </div>
          </div>

          {/* System Account Managers — per-technology default routing */}
          <div className="card shadow-sm mt-4">
            <div className="card-header bg-dark text-white d-flex justify-content-between align-items-center">
              <h6 className="mb-0">
                <i className="fas fa-user-shield me-2"></i>
                System Account Managers <span className="text-white-50">(Default Routing)</span>
              </h6>
              <button
                className="btn btn-sm btn-outline-light"
                onClick={() => applySysMgrDefaults(true)}
                disabled={sysMgrApplying || !sysMgrDefaults.some(r => r.attester_id)}
                title="Preview how many system accounts each default would route"
              >
                <i className="fas fa-eye me-2"></i>
                {sysMgrApplying ? 'Working…' : 'Show Coverage'}
              </button>
            </div>
            <div className="card-body">
              <p className="text-muted mb-3">
                Pick a default manager per technology. Campaigns automatically route any in-scope
                system / internal account to that manager — <strong>no per-account rows are
                created</strong>.
              </p>
              {/* Div-based rows (NOT a <table>): a SearchableDropdown's option
                  list is position:absolute, and inside a <td> the table's
                  stacking context lets rows BELOW bleed through the open list.
                  Plain stacked rows with descending z-index overlay cleanly. */}
              <div className="d-flex fw-bold text-uppercase small text-muted px-2 mb-1"
                   style={{ borderBottom: '2px solid #343a40' }}>
                <div style={{ width: '18%' }}>Technology</div>
                <div style={{ flex: 1 }}>Default Manager</div>
                <div style={{ width: '26%' }}>Resolved</div>
                <div style={{ width: '70px', textAlign: 'center' }}>Clear</div>
              </div>
              {sysMgrDefaults.map((row, idx) => (
                <div
                  key={row.database_type}
                  className="d-flex align-items-center px-2 py-2 border-bottom"
                  style={{ position: 'relative', zIndex: sysMgrDefaults.length - idx }}
                >
                  <div style={{ width: '18%' }}><strong>{row.database_type}</strong></div>
                  <div style={{ flex: 1, paddingRight: '1rem' }}>
                    <SearchableDropdown
                      options={enterpriseUsers}
                      value={row.attester_id || ''}
                      onChange={(value) => saveSysMgrDefault(row.database_type, value)}
                      placeholder="Type to search manager…"
                      valueField="employee_id"
                    />
                  </div>
                  <div style={{ width: '26%' }}>
                    {row.attester_id ? (
                      <span className="text-success">
                        <i className="fas fa-user-check me-1"></i>
                        {row.attester_name || row.attester_id}
                      </span>
                    ) : (
                      <span className="text-muted">Not set</span>
                    )}
                  </div>
                  <div style={{ width: '70px', textAlign: 'center' }}>
                    {row.attester_id && (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => saveSysMgrDefault(row.database_type, '')}
                        disabled={sysMgrSaving === row.database_type}
                        title="Clear this technology's default manager"
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {/* Coverage preview */}
              {sysMgrApplyResult && sysMgrApplyResult.success && (
                <div className="alert alert-info mt-3 mb-0">
                  <div className="fw-bold mb-2">
                    <i className="fas fa-eye me-2"></i>
                    {sysMgrApplyResult.message}
                  </div>
                  {sysMgrApplyResult.results.length === 0 ? (
                    <div className="text-muted">No technologies are configured yet — set a manager above first.</div>
                  ) : (
                    <table className="table table-sm table-bordered mb-0 bg-white">
                      <thead className="table-dark">
                        <tr>
                          <th>Technology</th>
                          <th>Default Manager</th>
                          <th>System accounts that would route here</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sysMgrApplyResult.results.map((r) => (
                          <tr key={r.database_type}>
                            <td>{r.database_type}</td>
                            <td>{r.attester_id}</td>
                            <td>{r.would_route ?? r.system_accounts_found}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
              {sysMgrApplyResult && !sysMgrApplyResult.success && (
                <div className="alert alert-danger mt-3 mb-0">{sysMgrApplyResult.error || 'Preview failed'}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="fas fa-plus me-2"></i>
                  Create Database Owner
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowCreateModal(false)}
                ></button>
              </div>
              <form onSubmit={handleCreateOwner}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Database Type *</label>
                      <select
                        className="form-select"
                        value={formData.database_type}
                        onChange={(e) => {
                          setFormData(prev => ({ ...prev, database_type: e.target.value, database_name: '', username: '' }));
                          setDatabaseAccounts([]);
                        }}
                        required
                      >
                        <option value="">Select type...</option>
                        {getUniqueDatabaseTypes().map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Database Name *</label>
                      <select
                        className="form-select"
                        value={formData.database_name}
                        onChange={(e) => {
                          const newDbName = e.target.value;
                          setFormData(prev => ({ ...prev, database_name: newDbName, username: '' }));
                          loadDatabaseAccounts(formData.database_type, newDbName);
                        }}
                        required
                        disabled={!formData.database_type}
                      >
                        <option value="">Select database...</option>
                        {formData.database_type && (
                          <option value="ALL">ALL (All databases of type {formData.database_type})</option>
                        )}
                        {getFilteredDatabases().map((db, index) => (
                          <option key={index} value={db.database_name}>
                            {formatDatabaseOption(db)}
                          </option>
                        ))}
                      </select>
                      {!formData.database_type && (
                        <small className="text-muted">Please select a database type first</small>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Username *</label>
                      {loadingAccounts ? (
                        <div className="form-control d-flex align-items-center">
                          <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                          Loading accounts...
                        </div>
                      ) : databaseAccounts.length > 0 ? (
                        <select
                          className="form-select"
                          value={formData.username}
                          onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                          required
                        >
                          <option value="">Select account...</option>
                          {databaseAccounts.map((account, index) => (
                            <option key={index} value={account.username}>
                              {account.username} {formData.database_name === 'ALL' ? `(${account.database_name})` : ''}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="form-control"
                          value={formData.username}
                          onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                          placeholder={formData.database_name ? "No accounts found - enter manually" : "Select database first"}
                          required
                        />
                      )}
                      {formData.database_name && databaseAccounts.length === 0 && !loadingAccounts && (
                        <small className="text-muted">No accounts found in source data. You can enter a username manually.</small>
                      )}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Attester ID *</label>
                      <SearchableDropdown
                        options={enterpriseUsers}
                        value={formData.attester_id}
                        onChange={(value) => setFormData(prev => ({ ...prev, attester_id: value }))}
                        placeholder="Type to search attester..."
                        valueField="employee_id"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.is_active}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value }))}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Effective Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.effective_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                      />
                      <small className="text-muted">Leave blank to use today's date</small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.expiry_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                      />
                      <small className="text-muted">Leave blank for no expiry</small>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Override Reason</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.override_reason}
                        onChange={(e) => setFormData(prev => ({ ...prev, override_reason: e.target.value }))}
                        placeholder="Enter the reason for this account owner override..."
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-save me-2"></i>
                    Create Owner
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header bg-primary text-white">
                <h5 className="modal-title">
                  <i className="fas fa-edit me-2"></i>
                  Edit Database Owner
                </h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>
              <form onSubmit={handleEditOwner}>
                <div className="modal-body">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Database Name *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.database_name}
                        disabled
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Database Type *</label>
                      <select
                        className="form-select"
                        value={formData.database_type}
                        disabled
                      >
                        <option value="">Select type...</option>
                        <option value="ORACLE">Oracle</option>
                        <option value="MYSQL">MySQL</option>
                        <option value="POSTGRESQL">PostgreSQL</option>
                        <option value="SQLSERVER">SQL Server</option>
                        <option value="MONGODB">MongoDB</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Username *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.username}
                        disabled
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Attester ID *</label>
                      <SearchableDropdown
                        options={enterpriseUsers}
                        value={formData.attester_id}
                        onChange={(value) => setFormData(prev => ({ ...prev, attester_id: value }))}
                        placeholder="Type to search attester..."
                        valueField="employee_id"
                        required
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Status</label>
                      <select
                        className="form-select"
                        value={formData.is_active}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value }))}
                      >
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Effective Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.effective_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                      />
                      <small className="text-muted">Leave blank to use today's date</small>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={formData.expiry_date}
                        onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                      />
                      <small className="text-muted">Leave blank for no expiry</small>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Override Reason</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={formData.override_reason}
                        onChange={(e) => setFormData(prev => ({ ...prev, override_reason: e.target.value }))}
                        placeholder="Enter the reason for this account owner override..."
                      />
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    <i className="fas fa-save me-2"></i>
                    Update Owner
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* System Account Managers — inline grid below Database Owners.
          Replaces the former WideModal. Per-tech defaults are routed at
          campaign time; this grid configures them. */}

      {/* Bulk Assign Modal */}
      {showBulkModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 1050, overflow: 'auto' }}>
          <div style={{ padding: '20px' }}>
            <div className="modal-content" style={{ maxWidth: '1400px', width: '100%', margin: '0 auto' }}>
              <div className="modal-header bg-warning text-dark">
                <h5 className="modal-title">
                  <i className="fas fa-users-cog me-2"></i>
                  Bulk Assign Database Owners
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowBulkModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <label className="form-label">Database Type *</label>
                    <select
                      className="form-select"
                      value={bulkFormData.database_type}
                      onChange={(e) => {
                        setBulkFormData(prev => ({ ...prev, database_type: e.target.value, database_name: '' }));
                        setBulkAccounts([]);
                        setSelectedBulkAccounts([]);
                      }}
                    >
                      <option value="">Select type...</option>
                      {getUniqueDatabaseTypes().map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Database Name *</label>
                    <select
                      className="form-select"
                      value={bulkFormData.database_name}
                      onChange={(e) => {
                        const newDbName = e.target.value;
                        setBulkFormData(prev => ({ ...prev, database_name: newDbName }));
                        loadBulkAccounts(bulkFormData.database_type, newDbName);
                      }}
                      disabled={!bulkFormData.database_type}
                    >
                      <option value="">Select database...</option>
                      {bulkFormData.database_type && (
                        <option value="ALL">ALL (All databases of type {bulkFormData.database_type})</option>
                      )}
                      {databases
                        .filter(db => db.database_type === bulkFormData.database_type)
                        .map((db, index) => (
                          <option key={index} value={db.database_name}>
                            {formatDatabaseOption(db)}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div className="col-md-4">
                    <label className="form-label">Attester *</label>
                    <SearchableDropdown
                      options={enterpriseUsers}
                      value={bulkFormData.attester_id}
                      onChange={(value) => setBulkFormData(prev => ({ ...prev, attester_id: value }))}
                      placeholder="Type to search attester..."
                      valueField="employee_id"
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Override Reason</label>
                    <input
                      type="text"
                      className="form-control"
                      value={bulkFormData.override_reason}
                      onChange={(e) => setBulkFormData(prev => ({ ...prev, override_reason: e.target.value }))}
                      placeholder="Enter the reason for this bulk assignment..."
                    />
                  </div>
                </div>

                {/* Account Selection */}
                {bulkFormData.database_name && (
                  <div className="card">
                    <div className="card-header bg-secondary text-white d-flex justify-content-between align-items-center">
                      <h6 className="mb-0">
                        <i className="fas fa-users me-2"></i>
                        Select Accounts ({selectedBulkAccounts.length} of {bulkAccounts.length} selected)
                      </h6>
                      <div className="btn-group btn-group-sm">
                        <button
                          type="button"
                          className="btn btn-outline-light"
                          onClick={handleSelectAllSystemAccounts}
                          title="Select all system/default accounts"
                        >
                          <i className="fas fa-robot me-1"></i>
                          System Only
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-light"
                          onClick={handleSelectAllAccounts}
                          title="Select all accounts"
                        >
                          <i className="fas fa-check-double me-1"></i>
                          All
                        </button>
                        <button
                          type="button"
                          className="btn btn-outline-light"
                          onClick={handleDeselectAllAccounts}
                          title="Deselect all accounts"
                        >
                          <i className="fas fa-times me-1"></i>
                          None
                        </button>
                      </div>
                    </div>
                    <div className="card-body p-0" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      {loadingBulkAccounts ? (
                        <div className="text-center py-4">
                          <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <div className="mt-2">Loading accounts...</div>
                        </div>
                      ) : bulkAccounts.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          <i className="fas fa-inbox fa-2x mb-2"></i>
                          <div>No accounts found for the selected database</div>
                        </div>
                      ) : (
                        <table className="table table-hover mb-0">
                          <thead className="table-dark sticky-top">
                            <tr>
                              <th style={{ width: '50px' }}>
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  checked={selectedBulkAccounts.length === bulkAccounts.length && bulkAccounts.length > 0}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      handleSelectAllAccounts();
                                    } else {
                                      handleDeselectAllAccounts();
                                    }
                                  }}
                                />
                              </th>
                              <th>Username</th>
                              <th>Database</th>
                              <th>Type</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkAccounts.map((account, index) => {
                              const key = `${account.username}|${account.database_name}`;
                              const isSelected = selectedBulkAccounts.includes(key);
                              return (
                                <tr
                                  key={index}
                                  className={isSelected ? 'table-primary' : ''}
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => handleBulkAccountToggle(account.username, account.database_name)}
                                >
                                  <td>
                                    <input
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={isSelected}
                                      onChange={() => handleBulkAccountToggle(account.username, account.database_name)}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </td>
                                  <td>
                                    <code>{account.username}</code>
                                  </td>
                                  <td>{account.database_name}</td>
                                  <td>
                                    {account.is_system_account ? (
                                      <span className="text-warning">
                                        <i className="fas fa-robot me-1"></i>
                                        System
                                      </span>
                                    ) : (
                                      <span className="text-muted">User</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}

                {!bulkFormData.database_name && bulkFormData.database_type && (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    Please select a database to view available accounts.
                  </div>
                )}

                {!bulkFormData.database_type && (
                  <div className="alert alert-info">
                    <i className="fas fa-info-circle me-2"></i>
                    Please select a database type to begin bulk assignment.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowBulkModal(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleBulkCreate}
                  disabled={!bulkFormData.attester_id || selectedBulkAccounts.length === 0}
                >
                  <i className="fas fa-save me-2"></i>
                  Assign {selectedBulkAccounts.length} Owner{selectedBulkAccounts.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatabaseOwner;